import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  GAMES_COMFORT_WIDTH,
  clampGamesWidth,
  gamesViewportMax,
  snapGamesRelease,
  snapWidthForMode,
} from "../constants";
import { useGamesPortalStore } from "../store/games-portal-store";

/**
 * Oyun panelini sol kenarından tut-sürükle ile boyutlar.
 * Bırakınca compact / comfort / full manyetik snap uygulanır.
 */
export const useGamesPanelResize = (enabled: boolean) => {
  const panelWidth = useGamesPortalStore((state) => state.panelWidth);
  const setPanelWidth = useGamesPortalStore((state) => state.setPanelWidth);
  const setSizeMode = useGamesPortalStore((state) => state.setSizeMode);
  const isFullscreen = useGamesPortalStore((state) => state.isFullscreen);
  const panelSide = useGamesPortalStore((state) => state.panelSide);

  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const widthRef = useRef(panelWidth);
  widthRef.current = panelWidth;

  const applyWidth = useCallback(
    (next: number, persistMode = true) => {
      const max = gamesViewportMax(window.innerWidth);
      const clamped = clampGamesWidth(next, max);
      setPanelWidth(clamped);
      if (persistMode) {
        const snapped = snapGamesRelease(clamped, window.innerWidth);
        if (snapped.mode !== "full") {
          useGamesPortalStore.setState({ sizeMode: snapped.mode });
        }
      }
      return clamped;
    },
    [setPanelWidth]
  );

  useEffect(() => {
    const constrain = () => {
      const max = gamesViewportMax(window.innerWidth);
      if (widthRef.current > max) {
        setPanelWidth(clampGamesWidth(widthRef.current, max));
      }
    };
    constrain();
    window.addEventListener("resize", constrain);
    return () => window.removeEventListener("resize", constrain);
  }, [setPanelWidth]);

  useEffect(() => {
    if (!isDragging) return;

    const onMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const delta =
        panelSide === "right"
          ? drag.startX - event.clientX
          : event.clientX - drag.startX;
      const next = drag.startWidth + delta;
      const max = gamesViewportMax(window.innerWidth);
      setPanelWidth(clampGamesWidth(next, max));
    };

    const onUp = () => {
      const snapped = snapGamesRelease(widthRef.current, window.innerWidth);
      dragRef.current = null;
      setIsDragging(false);
      document.body.style.removeProperty("cursor");
      document.body.style.removeProperty("user-select");
      if (snapped.mode === "full") {
        setSizeMode("full");
        return;
      }
      setPanelWidth(snapped.width);
      setSizeMode(snapped.mode);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [isDragging, panelSide, setPanelWidth, setSizeMode]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (!enabled || event.button !== 0) return;
      event.preventDefault();
      const startWidth = isFullscreen
        ? snapWidthForMode("comfort")
        : widthRef.current;
      if (isFullscreen) {
        setSizeMode("comfort");
      }
      dragRef.current = { startX: event.clientX, startWidth };
      setIsDragging(true);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [enabled, isFullscreen, setSizeMode]
  );

  const onDoubleClick = useCallback(() => {
    setSizeMode("comfort");
  }, [setSizeMode]);

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLButtonElement>) => {
      const step = event.shiftKey ? 48 : 16;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        applyWidth(
          widthRef.current + (panelSide === "right" ? step : -step)
        );
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        applyWidth(
          widthRef.current + (panelSide === "right" ? -step : step)
        );
      } else if (event.key === "Home") {
        event.preventDefault();
        setSizeMode("comfort");
      }
    },
    [applyWidth, panelSide, setSizeMode]
  );

  return {
    width: panelWidth,
    isDragging,
    handleProps: {
      onPointerDown,
      onDoubleClick,
      onKeyDown,
    },
    comfortWidth: GAMES_COMFORT_WIDTH,
  };
};
